import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { OrgSwitcher } from "./OrgSwitcher.js";
import { UnreadBadge } from "./UnreadBadge.js";
import { Wordmark } from "./Wordmark.js";
import { useAuth } from "../lib/auth-context.js";

const navLink: React.CSSProperties = {
  color: "var(--text-secondary)",
  textDecoration: "none",
  letterSpacing: "0.08em",
  fontSize: 12,
};

const DEFAULT_MAIN_WIDTH = 1100;

/**
 * Lets a page ask for a wider content column than the default.
 *
 * The device page uses it to fit the discussion sidebar beside the charts
 * instead of squeezing them: at 1100px a 340px sidebar would leave the charts
 * barely wider than a phone. Reset on unmount, so leaving the page cannot
 * strand every other page at the wide setting.
 */
const MainWidthContext = createContext<(width: number | null) => void>(() => {});

export function useMainWidth(width: number | null): void {
  const set = useContext(MainWidthContext);
  useEffect(() => {
    set(width);
    return () => set(null);
  }, [set, width]);
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const [mainWidth, setMainWidth] = useState<number | null>(null);

  return (
    <div style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
          borderBottom: "1px solid var(--border)",
          background: "var(--surface-1)",
        }}
      >
        {/* Static here: replaying the unpack on every navigation would
            wear out fast. The login screen is where it earns its keep. */}
        <Link
          to="/"
          style={{ color: "var(--text-primary)", textDecoration: "none", textShadow: "0 0 4px currentColor" }}
        >
          <Wordmark />
        </Link>
        {user && !user.mustChangePassword && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13 }}>
            <UnreadBadge />
            <OrgSwitcher activeName={user.tenant.name} />
            <Link to="/" style={navLink}>DEVICES</Link>
            <Link to="/alerts" style={navLink}>ALERTS</Link>
            <Link to="/connectors" style={navLink}>CONNECTORS</Link>
            <Link to="/organizations" style={navLink}>ORGS</Link>
            {/* Only for platform operators; the API 404s for everyone else. */}
            {user.isPlatformAdmin && (
              <Link to="/platform" style={{ ...navLink, color: "var(--status-warning)" }}>PLATFORM</Link>
            )}
            {user.canManageUsers && (
              <Link to="/users" style={navLink}>USERS</Link>
            )}
            <Link to="/account/password" style={{ ...navLink, color: "var(--text-muted)" }}>
              {user.email}
            </Link>
            <button
              onClick={() => void logout()}
              style={{
                background: "none",
                border: "1px solid var(--border)",
                padding: "5px 10px",
                fontSize: 12,
                color: "var(--text-secondary)",
                cursor: "pointer",
                letterSpacing: "0.08em",
              }}
            >
              [ LOGOUT ]
            </button>
          </div>
        )}
      </header>
      <main style={{ flex: 1, padding: 20, maxWidth: mainWidth ?? DEFAULT_MAIN_WIDTH, width: "100%", margin: "0 auto" }}>
        <MainWidthContext.Provider value={setMainWidth}>{children}</MainWidthContext.Provider>
      </main>
    </div>
  );
}
